import React from 'react'
import {
  Card,
  CardActions,
  CardHeader,
  CardText
} from 'material-ui/Card'
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderColumn,
  TableRow,
  TableRowColumn
} from 'material-ui/Table'
import Dialog from 'material-ui/Dialog'
import RaisedButton from 'material-ui/RaisedButton'
import FlatButton from 'material-ui/FlatButton'
import IconMenu from 'material-ui/IconMenu'
import IconButton from 'material-ui/IconButton'
import MoreVertIcon from 'material-ui/svg-icons/navigation/more-vert'
import MenuItem from 'material-ui/MenuItem'

import api from '../../../../api'
import { formatCurrency, formatDateTime } from '../../../../formatters'
import CreatePayment from './create-payment'
import CreateRefund from './create-refund'
import ListRefunds from './list-refunds'
import VoidTransactionDialog from './void-transaction-dialog'

const styles = {
  menuColumn: {
    width: '50px'
  },
  dateColumn: {
    width: '200px'
  },
  amountColumn: {
    width: '70px'
  },
  refundColumn: {
    width: '70px'
  },
  refundDialogContent: {
    width: '100%',
    maxWidth: 'none'
  }
}

class OrderPayments extends React.Component<any, any> {
  constructor (props) {
    super(props)
    this.state = {
      isLoaded: false,
      openDialog: false,
      selectedPayment: null
    }
  }

  componentWillMount () {
    this.loadPayments()
      .then(payments => this.loadRefunds(payments))
      .then(() => {
        this.setState({ isLoaded: true })
      })
  }

  componentWillReceiveProps (nextProps) {
    if ((nextProps.lastUpdatedAt && !this.props.lastUpdatedAt) ||
        (nextProps.lastUpdatedAt && this.props.lastUpdatedAt &&
          nextProps.lastUpdatedAt > this.props.lastUpdatedAt)) {
      this.loadPayments()
    }
  }

  loadPayments () {
    const body = {
      filters: {
        order_id: this.props.order.id,
        voided_at_null: !this.props.isVoid
      },
      order: [{
        column: 'id',
        direction: 'asc'
      }]
    }
    return api.get('admin/payments', { body })
      .then(res => res.json())
      .then(payments => {
        this.setState({ payments })
        return payments
      })
  }

  loadRefunds (payments) {
    const body = {
      filters: {
        payment_id_in: payments.map(payment => payment.id)
      }
    }

    return api.get('admin/refunds', { body })
      .then(res => res.json())
      .then(refunds => {
        this.setState({ refunds })
        return refunds
      })
  }

  openPaymentDialog = () => {
    this.setState({ openDialog: 'payment' })
  }

  openRefundDialog = selectedPayment => {
    this.setState({
      openDialog: 'refund',
      selectedPayment
    })
  }

  openRefundsDialog = selectedPayment => {
    this.setState({
      openDialog: 'refunds',
      selectedPayment
    })
  }

  openVoidDialog = selectedPayment => {
    this.setState({
      openDialog: 'void',
      selectedPayment
    })
  }

  handlePaymentCreated = payment => {
    this.props.onOrderChanged()
    this.setState({
      openDialog: null,
      payments: this.state.payments.concat(payment)
    })
  }

  handleRefundCreated = refund => {
    this.props.onOrderChanged()

    const refunds = this.state.refunds.concat(refund)
    let payments = this.state.payments

    if (refund.credit_memo) {
      payments = payments.concat(refund.credit_memo)
    }

    this.setState({
      openDialog: null,
      refunds: refunds,
      payments: payments,
      selectedPayment: null
    })
  }

  closeDialog = () => {
    this.setState({
      openDialog: null,
      selectedPayment: null
    })
  }

  render () {
    if (!this.state.isLoaded) {
      return null
    }

    const { payments, refunds } = this.state
    const { order, isVoid } = this.props

    if (!payments) {
      return null
    }

    const findPaymentRefunds = payment => {
      return payment
        ? refunds.filter(refund => refund.payment_id === payment.id)
        : []
    }

    return (
      (isVoid && payments.length > 0) || (!isVoid)
        ? <Card>
          <CardHeader title={isVoid ? 'Void Payments' : 'Payments'} />
          <CardText>
            <Table selectable={false}>
              <TableHeader adjustForCheckbox={false} displaySelectAll={false}>
                <TableRow>
                  <TableHeaderColumn style={styles.dateColumn}>
                  Date
                  </TableHeaderColumn>
                  <TableHeaderColumn>
                  Reference #
                  </TableHeaderColumn>
                  <TableHeaderColumn style={styles.amountColumn}>
                  Amount
                  </TableHeaderColumn>
                  <TableHeaderColumn style={styles.refundColumn} />
                  <TableHeaderColumn style={styles.menuColumn} />
                </TableRow>
              </TableHeader>
              <TableBody displayRowCheckbox={false}>
                {payments.map(payment => {
                  const paymentRefunds = findPaymentRefunds(payment)

                  return (
                    <TableRow key={payment.id}>
                      <TableRowColumn style={styles.dateColumn}>
                        {formatDateTime(payment.payment_date)}
                      </TableRowColumn>
                      <TableRowColumn>
                        {payment.reference_number}
                      </TableRowColumn>
                      <TableRowColumn style={styles.amountColumn}>
                        {formatCurrency(payment.amount)}
                      </TableRowColumn>
                      <TableRowColumn style={styles.refundColumn}>
                        {
                          (paymentRefunds.length > 0 && !isVoid) &&
                            <FlatButton
                              secondary
                              label='Refunded'
                              onClick={() => this.openRefundsDialog(payment)}
                            />
                        }
                      </TableRowColumn>
                      <TableRowColumn style={styles.menuColumn}>
                        <IconMenu
                          iconButtonElement={<IconButton><MoreVertIcon /></IconButton>}
                        >
                          {!isVoid &&
                            <MenuItem
                              primaryText='Refund'
                              onClick={() => this.openRefundDialog(payment)}
                            />}
                          <MenuItem
                            primaryText={isVoid ? 'Revalidate' : 'Void'}
                            onClick={() => this.openVoidDialog(payment)}
                          />
                        </IconMenu>
                      </TableRowColumn>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </CardText>
          <CardActions>
            {!isVoid &&
              <RaisedButton
                primary
                label='Create Payment'
                onClick={this.openPaymentDialog}
              />}
            <Dialog
              modal={false}
              open={this.state.openDialog === 'payment'}
            >
              <CreatePayment
                order={order}
                onPaymentCreated={this.handlePaymentCreated}
                onPaymentCanceled={this.closeDialog}
              />
            </Dialog>
            <Dialog
              modal={false}
              open={this.state.openDialog === 'refund'}
            >
              <CreateRefund
                order={order}
                payment={this.state.selectedPayment}
                refunds={refunds}
                onRefundCreated={this.handleRefundCreated}
                onRefundCanceled={this.closeDialog}
              />
            </Dialog>
            <Dialog
              modal={false}
              open={this.state.openDialog === 'refunds'}
              onRequestClose={this.closeDialog}
              contentStyle={styles.refundDialogContent}
            >
              <ListRefunds
                refunds={findPaymentRefunds(this.state.selectedPayment)}
                onRefundUpdated={() => this.loadRefunds(this.state.payments)}
              />
            </Dialog>
            <VoidTransactionDialog
// @ts-ignore
              open={this.state.openDialog === 'void'}
              isVoid={isVoid}
              transaction={this.state.selectedPayment}
              transactionLabel='payment'
              closeDialog={this.closeDialog}
              onPaymentUpdated={() => this.props.handlePaymentsChanged(new Date())}
            />
          </CardActions>
        </Card>
        : null
    )
  }
}

// @ts-ignore
OrderPayments.propTypes = {
  order: React.PropTypes.object,
  onOrderChanged: React.PropTypes.func.isRequired,
  isVoid: React.PropTypes.bool
}

export default OrderPayments
